(function () {
  const SECTION_SELECTOR = '.product-reviews';

  function capitalizeName(value) {
    if (!value) return '';
    return value
      .trim()
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  function wrapQuote(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('"') || trimmed.startsWith('\u201c')) return trimmed;
    return `"${trimmed}"`;
  }

  function extractLocation(customFields) {
    if (!customFields) return '';

    const locationSelectors = [
      '.jdgm-rev__cf-ans--location',
      '.jdgm-rev__cf-ans--city',
      '[data-answer-label="Location"] .jdgm-rev__cf-ans',
      '[data-answer-label="City"] .jdgm-rev__cf-ans'
    ];

    for (const selector of locationSelectors) {
      const node = customFields.querySelector(selector);
      if (node && node.textContent.trim()) {
        return node.textContent.trim();
      }
    }

    const rows = customFields.querySelectorAll('.jdgm-rev__cf-ans, .jdgm-rev__cf-ans__value');
    for (const row of rows) {
      const label = row.closest('.jdgm-rev__cf-ans-row, .jdgm-rev__cf-ans')?.querySelector('.jdgm-rev__cf-ans__label');
      const labelText = (label?.textContent || '').toLowerCase();
      if (labelText.includes('location') || labelText.includes('city')) {
        const value = row.textContent.trim();
        if (value) return value;
      }
    }

    return '';
  }

  function extractTags(customFields) {
    if (!customFields) return [];

    const tags = new Set();
    const tagSelectors = [
      '.jdgm-rev__cf-ans--tags',
      '[data-answer-label="Tags"] .jdgm-rev__cf-ans',
      '.jdgm-rev__cf-ans--tag'
    ];

    tagSelectors.forEach((selector) => {
      customFields.querySelectorAll(selector).forEach((node) => {
        node.textContent
          .split(/[,|]/)
          .map((part) => part.trim())
          .filter(Boolean)
          .forEach((tag) => tags.add(tag));
      });
    });

    customFields.querySelectorAll('.jdgm-rev__cf-ans-row').forEach((row) => {
      const label = row.querySelector('.jdgm-rev__cf-ans__label');
      const value = row.querySelector('.jdgm-rev__cf-ans__value, .jdgm-rev__cf-ans');
      const labelText = (label?.textContent || '').toLowerCase();
      if (!labelText.includes('tag') || !value) return;

      value.textContent
        .split(/[,|]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((tag) => tags.add(tag));
    });

    return Array.from(tags);
  }

  function normalizeReviewText(text) {
    return (text || '')
      .trim()
      .replace(/^["“]|["”]$/g, '')
      .replace(/\s+/g, ' ');
  }

  function getWordCount(text) {
    const normalized = normalizeReviewText(text);
    if (!normalized) return 0;
    return normalized.split(' ').filter(Boolean).length;
  }

  function isLowQualityReview(body, title) {
    const primary = body || title;
    if (!primary) return true;

    const compact = primary.replace(/\s+/g, '').toLowerCase();

    if (compact.length <= 4) return true;
    if (compact.length >= 3 && /^(.)\1+$/.test(compact)) return true;

    if (body && title && body.toLowerCase() === title.toLowerCase() && compact.length < 15) {
      return true;
    }

    if (primary.length < 12 && getWordCount(primary) < 2) {
      return true;
    }

    return false;
  }

  function getReviewText(review) {
    const bodyNode = review.querySelector('.jdgm-rev__body, .reviews-fourth__quote');
    const titleNode = review.querySelector('.jdgm-rev__title, .reviews-fourth__author-name');

    const body = normalizeReviewText(bodyNode?.textContent || '');
    const title = normalizeReviewText(titleNode?.textContent || '');

    return { body, title };
  }

  function shouldHideReview(review) {
    if (!review) return true;
    if (review.classList.contains('jdgm-hidden') || review.classList.contains('jdgm-temp-hidden')) {
      return true;
    }

    const { body, title } = getReviewText(review);
    return isLowQualityReview(body, title);
  }

  function filterReview(review) {
    if (!review) return true;

    if (shouldHideReview(review)) {
      review.classList.add('jdgm-hidden', 'product-reviews__filtered');
      review.setAttribute('aria-hidden', 'true');
      review.dataset.productReviewsFiltered = 'true';
      return true;
    }

    review.classList.remove('product-reviews__filtered');
    review.removeAttribute('aria-hidden');
    delete review.dataset.productReviewsFiltered;
    return false;
  }

  function parseAuthorFromTitle(titleText) {
    const title = (titleText || '').trim();
    if (!title) return { name: '', detail: '' };

    const parts = title.split(',').map((part) => part.trim());
    if (parts.length < 2) {
      return { name: title, detail: '' };
    }

    const name = parts[0];
    const detail = parts.slice(1).join(', ');
    const ageMatch = detail.match(/^\d{1,3}$/);

    return {
      name,
      detail: ageMatch ? `${detail} years old` : detail
    };
  }

  function formatReviewDate(timestampNode) {
    if (!timestampNode) return null;

    const raw =
      timestampNode.getAttribute('data-content') ||
      timestampNode.dataset.content ||
      timestampNode.textContent ||
      '';
    const cleaned = raw.trim();
    if (!cleaned) return null;

    const parsed = new Date(cleaned);
    if (Number.isNaN(parsed.getTime())) {
      return { text: cleaned, datetime: cleaned };
    }

    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    const year = parsed.getUTCFullYear();

    return {
      text: `${month}/${day}/${year}`,
      datetime: cleaned
    };
  }

  function enhanceReview(review, config) {
    if (!review || review.dataset.productReviewsFiltered === 'true') return;
    if (review.dataset.productReviewsEnhanced === 'true') return;

    const authorNode = review.querySelector('.jdgm-rev__author');
    const titleNode = review.querySelector('.jdgm-rev__title');
    const bodyNode = review.querySelector('.jdgm-rev__body');
    const timestampNode = review.querySelector('.jdgm-rev__timestamp');
    const customFields = review.querySelector('.jdgm-rev__cf, .jdgm-rev__custom-form');
    const ratingNode = review.querySelector('.jdgm-rev__rating');

    if (!bodyNode && !authorNode && !titleNode) return;

    review.dataset.productReviewsEnhanced = 'true';
    review.classList.add('reviews-fourth__row', 'product-reviews__row');

    const parsedTitle = parseAuthorFromTitle(titleNode?.textContent || '');
    let authorName = parsedTitle.name || capitalizeName(authorNode?.textContent || '');
    if (authorName.toLowerCase() === 'anonymous' && parsedTitle.name) {
      authorName = capitalizeName(parsedTitle.name);
    }

    const authorCol = document.createElement('div');
    authorCol.className = 'reviews-fourth__author product-reviews__author';

    if (authorName) {
      const nameEl = document.createElement('p');
      nameEl.className = 'reviews-fourth__author-name';
      nameEl.textContent = authorName;
      authorCol.appendChild(nameEl);
    }

    const location = extractLocation(customFields) || parsedTitle.detail;
    if (location) {
      const locationEl = document.createElement('p');
      locationEl.className = 'reviews-fourth__author-location';
      locationEl.textContent = location;
      authorCol.appendChild(locationEl);
    }

    const verifiedEl = document.createElement('p');
    verifiedEl.className = 'reviews-fourth__verified';
    verifiedEl.innerHTML = `
      <img
        src="${config.verifiedIconUrl}"
        alt=""
        width="17"
        height="17"
        loading="lazy"
        class="reviews-fourth__verified-icon"
      >
      <span>${config.verifiedLabel}</span>
    `;
    authorCol.appendChild(verifiedEl);

    const contentCol = document.createElement('div');
    contentCol.className = 'reviews-fourth__content product-reviews__content';

    const meta = document.createElement('div');
    meta.className = 'reviews-fourth__content-meta';

    const stars = document.createElement('div');
    stars.className = 'reviews-fourth__row-stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.innerHTML = `<img src="${config.starsRowUrl}" alt="" width="81" height="15" loading="lazy">`;
    meta.appendChild(stars);

    if (ratingNode) {
      const score = ratingNode.getAttribute('data-score') || ratingNode.dataset.score;
      if (score) {
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = `${score} out of 5 stars`;
        meta.appendChild(sr);
      }
    }

    if (timestampNode) {
      const formattedDate = formatReviewDate(timestampNode);
      if (formattedDate) {
        const date = document.createElement('time');
        date.className = 'reviews-fourth__date';
        date.textContent = formattedDate.text;
        date.setAttribute('datetime', formattedDate.datetime);
        meta.appendChild(date);
      }
    }

    contentCol.appendChild(meta);

    const quoteText = bodyNode?.textContent?.trim() || '';
    if (quoteText) {
      const quote = document.createElement('blockquote');
      quote.className = 'reviews-fourth__quote';
      const paragraph = document.createElement('p');
      paragraph.textContent = wrapQuote(quoteText);
      quote.appendChild(paragraph);
      contentCol.appendChild(quote);
    }

    const tags = extractTags(customFields);
    if (tags.length) {
      const tagsWrap = document.createElement('div');
      tagsWrap.className = 'reviews-fourth__tags';
      tags.forEach((tag) => {
        const tagEl = document.createElement('span');
        tagEl.className = 'reviews-fourth__tag';
        tagEl.textContent = tag;
        tagsWrap.appendChild(tagEl);
      });
      contentCol.appendChild(tagsWrap);
    }

    review.replaceChildren(authorCol, contentCol);
  }

  function getListShell(section) {
    return section.querySelector('.product-reviews__list-shell');
  }

  function ensureListShell(section) {
    let shell = getListShell(section);
    if (shell) return shell;

    const widgets = section.querySelector('.product-reviews__widgets');
    if (!widgets || !widgets.parentNode) return null;

    shell = document.createElement('div');
    shell.className = 'product-reviews__list-shell';

    const skeleton = document.createElement('div');
    skeleton.className = 'product-reviews__skeleton';
    skeleton.hidden = true;
    skeleton.setAttribute('aria-hidden', 'true');

    widgets.parentNode.insertBefore(shell, widgets);
    shell.appendChild(skeleton);
    shell.appendChild(widgets);

    return shell;
  }

  function getSkeletonHost(section) {
    ensureListShell(section);
    return section.querySelector('.product-reviews__skeleton');
  }

  function getVisibleReviewCount(section) {
    const perPage = section.querySelector('.jdgm-paginate')?.dataset.perPage;
    const perPageCount = Number.parseInt(perPage || '', 10);

    const enhanced = section.querySelectorAll(
      '.jdgm-rev[data-product-reviews-enhanced="true"]:not([data-product-reviews-filtered="true"])'
    ).length;
    if (enhanced) return enhanced;

    const reviews = section.querySelectorAll('.jdgm-rev:not([data-product-reviews-filtered="true"])').length;
    if (reviews) return reviews;
    if (Number.isFinite(perPageCount) && perPageCount > 0) return perPageCount;

    return 5;
  }

  function createSkeletonRow() {
    const row = document.createElement('div');
    row.className = 'product-reviews__skeleton-row reviews-fourth__row';
    row.setAttribute('aria-hidden', 'true');
    row.innerHTML = `
      <div class="product-reviews__skeleton-author">
        <span class="product-reviews__skeleton-line product-reviews__skeleton-line--name"></span>
        <span class="product-reviews__skeleton-line product-reviews__skeleton-line--location"></span>
        <span class="product-reviews__skeleton-line product-reviews__skeleton-line--verified"></span>
      </div>
      <div class="product-reviews__skeleton-content">
        <div class="product-reviews__skeleton-meta">
          <span class="product-reviews__skeleton-line product-reviews__skeleton-line--stars"></span>
          <span class="product-reviews__skeleton-line product-reviews__skeleton-line--date"></span>
        </div>
        <span class="product-reviews__skeleton-line product-reviews__skeleton-line--quote"></span>
        <span class="product-reviews__skeleton-line product-reviews__skeleton-line--quote product-reviews__skeleton-line--short"></span>
      </div>
    `;
    return row;
  }

  function saveScrollAnchor(section) {
    const shell = getListShell(section) || ensureListShell(section);
    if (!shell) return;

    section._paginationAnchorTop = shell.getBoundingClientRect().top;
  }

  function restoreScrollAnchor(section) {
    const shell = getListShell(section);
    const anchorTop = section._paginationAnchorTop;
    if (!shell || !Number.isFinite(anchorTop)) return;

    const drift = shell.getBoundingClientRect().top - anchorTop;
    if (Math.abs(drift) > 1) {
      window.scrollTo(0, window.scrollY + drift);
    }
  }

  function showPaginationLoading(section) {
    if (section.classList.contains('is-pagination-loading')) return;

    const shell = ensureListShell(section);
    const skeleton = getSkeletonHost(section);
    if (!shell || !skeleton) return;

    saveScrollAnchor(section);

    const reviewCount = getVisibleReviewCount(section);
    const lockedHeight = shell.offsetHeight;

    if (lockedHeight > 0) {
      shell.style.minHeight = `${lockedHeight}px`;
    }

    section.classList.add('is-pagination-loading');
    section.setAttribute('aria-busy', 'true');

    skeleton.replaceChildren();
    for (let index = 0; index < reviewCount; index += 1) {
      skeleton.appendChild(createSkeletonRow());
    }
    skeleton.hidden = false;

    clearTimeout(section._paginationFallbackTimer);
    section._paginationFallbackTimer = window.setTimeout(() => {
      hidePaginationLoading(section);
    }, 8000);
  }

  function hidePaginationLoading(section) {
    if (!section.classList.contains('is-pagination-loading')) return;

    clearTimeout(section._paginationFallbackTimer);
    section._paginationFallbackTimer = null;

    section.classList.remove('is-pagination-loading');
    section.removeAttribute('aria-busy');

    const shell = getListShell(section);
    const skeleton = getSkeletonHost(section);

    if (skeleton) {
      skeleton.hidden = true;
      skeleton.replaceChildren();
    }

    restoreScrollAnchor(section);

    if (shell) {
      shell.style.minHeight = '';
    }

    requestAnimationFrame(() => {
      restoreScrollAnchor(section);
      delete section._paginationAnchorTop;
    });
  }

  function bindPagination() {
    if (window._productReviewsPaginationBound === 'true') return;
    window._productReviewsPaginationBound = 'true';

    document.addEventListener(
      'mousedown',
      (event) => {
        const control = event.target.closest(
          '.jdgm-paginate__page, .jdgm-paginate__prev-page, .jdgm-paginate__next-page'
        );
        if (!control || control.classList.contains('jdgm-curt') || control.classList.contains('jdgm-disabled')) {
          return;
        }

        const section = control.closest('.product-reviews');
        if (!section) return;

        saveScrollAnchor(section);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const control = event.target.closest(
          '.jdgm-paginate__page, .jdgm-paginate__prev-page, .jdgm-paginate__next-page'
        );
        if (!control || control.classList.contains('jdgm-curt') || control.classList.contains('jdgm-disabled')) {
          return;
        }

        const section = control.closest('.product-reviews');
        if (!section) return;

        window.setTimeout(() => control.blur(), 0);
        showPaginationLoading(section);
      },
      true
    );
  }

  function runEnhance(section) {
    const widget = section.querySelector('.jdgm-review-widget, .jdgm-rev-widg');
    if (!widget) return false;

    const reviews = section.querySelectorAll('.jdgm-rev');
    if (!reviews.length) return false;

    const config = {
      starsRowUrl: section.dataset.starsRowUrl,
      verifiedIconUrl: section.dataset.verifiedIconUrl,
      verifiedLabel: section.dataset.verifiedLabel || 'Verified Juno User'
    };

    reviews.forEach((review) => {
      filterReview(review);
      if (review.dataset.productReviewsFiltered === 'true') return;
      enhanceReview(review, config);
    });

    if (section.classList.contains('is-pagination-loading')) {
      const visibleReviews = section.querySelectorAll('.jdgm-rev:not([data-product-reviews-filtered="true"])');
      const enhancedCount = section.querySelectorAll(
        '.jdgm-rev[data-product-reviews-enhanced="true"]:not([data-product-reviews-filtered="true"])'
      ).length;
      if (enhancedCount === visibleReviews.length) {
        hidePaginationLoading(section);
      }
    }

    return true;
  }

  function scheduleEnhance(section) {
    clearTimeout(section._enhanceDebounceTimer);
    section._enhanceDebounceTimer = window.setTimeout(() => {
      section._enhanceDebounceTimer = null;
      runEnhance(section);
    }, 150);
  }

  function watchSection(section) {
    if (section.dataset.productReviewsWatching === 'true') return;

    const mount = section.querySelector('.product-reviews__widgets');
    if (!mount) return;

    section.dataset.productReviewsWatching = 'true';

    const tryEnhance = () => {
      const widget = section.querySelector('.jdgm-review-widget, .jdgm-rev-widg');
      if (!widget) return false;
      scheduleEnhance(section);
      return true;
    };

    const observer = new MutationObserver(() => {
      scheduleEnhance(section);
    });

    observer.observe(mount, { childList: true, subtree: true });

    if (!tryEnhance() && window.jdgm) {
      try {
        window.jdgm.loadWidgets?.();
        window.jdgm.customize?.();
      } catch (error) {
        /* Judge.me may not be ready yet */
      }
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (tryEnhance() || attempts > 60) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  function boot(root) {
    bindPagination();
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(watchSection);
  }

  function waitForJudgeMe() {
    if (window.jdgm) {
      boot();
      return;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (window.jdgm || attempts > 40) {
        window.clearInterval(timer);
        boot();
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForJudgeMe);
  } else {
    waitForJudgeMe();
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();
